import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { WebApiService } from '../../web-api.service';
import { BaseConfig } from '../../base-config.service';
import { ClinicDataService } from '../../data-service/clinic-data-service';
import { RxOpt } from '../hist/hist-edit-option';
import { OrderTypeEnum } from 'src/app/enums/OrderTypeEnum';
import {
  MOUNTAIN_ISLAND_AREA_CODES,
  isMountainIslandArea,
  normalizeMountainIslandAreaCode
} from 'src/app/shared/helpers/mountain-island-area-helper';

// ============================================================
//  Interfaces — 代碼查詢
// ============================================================

export interface DentalCode {
  Code: string;
  Name: string;
  Points: number;
}

export interface ApiDentalCodeItem {
  name: string;
  points: string;
}

export interface ApiDentalCodeResponse {
  [code: string]: ApiDentalCodeItem;
}

// ============================================================
//  Interfaces — 加成規則 JSON 結構
// ============================================================

/** 單一加成類型定義 */
export interface BonusTypeDef {
  type: string;
  rate: number;
  label: string;
  conditions: {
    codePattern?: string;
    codes?: string[];
    exclusions?: string[];
    minAge?: number;
    maxAge?: number;
    excludeCodes?: string[];
    excludeExamFee?: boolean;
    caseTypes?: string[];
    nameContains?: string;
  };
}

/** 身障倍率級距 */
export interface DisabilityLevelsDef {
  profound_autism_dementia: number;
  severe: number;
  moderate: number;
  mild: number;
  lt: number;
}

/** 身障診察費代碼對照表 */
export interface DisabilityExamFeeMapDef {
  [examFeeCode: string]: number | 'flexible';
}

/** 全域排除規則 */
export interface GlobalExclusionsDef {
  neverAddition: string[];
  caseType14NoAdditions: string[];
  caseType14ExclusionsOnlyBase: string[];
  notForCaseType16: string[];
}

/** 青少年口腔照護 (LM) 判定規則 */
export interface LMRuleDef {
  description?: string;
  codes: string[];
  minAge: number;
  maxAge: number;
}

/** 牙科情境比對條件 */
export interface DentalScenarioMatch {
  isClinicLack?: boolean;
  isMobile?: boolean;
  isCommunityStation?: boolean;
  hasLMRx?: boolean;
  isDisability?: boolean;
}

/** 牙科情境套用結果 */
export interface DentalScenarioResult {
  finalCase: string;
  sptxOrdered: string[];
  multiplier: number | 'disability';
  p8: string | null;
}

export interface DentalScenarioDef {
  id: string;
  label: string;
  match: DentalScenarioMatch;
  result: DentalScenarioResult;
}

export interface DentalScenariosBlock {
  description?: string;
  mutualExclusions?: { [key: string]: string };
  list: DentalScenarioDef[];
}

export interface ResolvedDentalScenario {
  scenario: DentalScenarioDef;
  effectiveMultiplier: number;
  multiplierSource: 'scenario' | 'disability' | 'island-holiday-override';
  sptxResolved: string[];
  p8: string | null;
}

/** 完整規則集 (對應 JSON 檔案) */
export interface AdditionRuleSet {
  version: string;
  effectiveDate: string;
  description: string;
  rules: {
    caseTypeMultipliers: { [caseType: string]: number };
    bonusTypes: BonusTypeDef[];
    disabilityMultipliers: {
      levels: DisabilityLevelsDef;
      treatmentExclusions: string[];
      caseType16TreatmentExclusions?: string[];
      examFeeCodeMap: DisabilityExamFeeMapDef;
    };
    globalExclusions: GlobalExclusionsDef;
    lmRule?: LMRuleDef;
    dentalScenarios?: DentalScenariosBlock;
  };
}

/** 局部更新 (Patch) 格式 */
export interface AdditionRulePatch {
  version: string;
  effectiveDate: string;
  description: string;
  patches: Array<{
    stdCode?: string;
    bonusType?: string;
    rate?: number;
    field?: string;
    action?: 'add' | 'remove' | 'update';
    value?: any;
  }>;
}

/** 統一的規則檔案格式 (base 或 patch) */
export type AdditionRuleFile = AdditionRuleSet | AdditionRulePatch;

// ============================================================
//  Interfaces — 加成計算
// ============================================================

/** 計算輸入上下文 */
export interface AdditionContext {
  stdCode: string;        // 標準健保碼
  caseType: string;       // 案件類別 (19/16/14/12)
  age: number;            // 患者年齡（整數歲）
  ageInMonths?: number;   // 患者月齡（就醫年月-出生年月），用於嬰幼兒精確判定
  isReferral: boolean;    // 是否轉診
  isHoliday: boolean;     // 是否假日
  isNight: boolean;       // 是否夜間
  visitDate: Date;        // 看診日 (Register.RegDate), 禁止使用 new Date()
  disabilityData?: DisabilityInput | null;
  codeName?: string;      // 代碼名稱 (輔助判斷)
  specialTreatment?: string; // 特治欄位 (舊版備用)
  cannotApplyPlusCodes?: string[]; // 後端排除清單
  sameDayCodes?: string[]; // 同一天已申報的其他處置 StdCode（用於跨碼聯動加成調整）
  isDental?: boolean; // 牙科門診情境才套用 dentalScenarios
  isClinicLack?: boolean; // 醫療資源不足院所
  isIsland?: boolean; // 山地離島，供 mutual exclusion 判斷
  specAreaSV?: string; // '02' 表示巡迴
  isCommunityStation?: boolean; // 社區醫療站
  hasLMRx?: boolean; // 同日是否含 LM 處置
  isDisability?: boolean; // 是否身障情境
}

/** 身障問卷輸入 (對應 DentalDisabilityFormDataOptimized) */
export interface DisabilityInput {
  disabilityScores?: string;
  selectedServiceMode?: string;
  matchedTreatmentItem?: string;
}

/** 統一的加成計算結果 */
export interface AdditionResult {
  plus: number;           // 最終倍率 (≥ 1.0)
  reasons: string[];      // 加成原因清單
  warnings: string[];     // 警告訊息 (如生效日異常)
}

// ============================================================
//  規則 JSON 檔案索引 (用於載入)
// ============================================================

/** 已知的規則檔清單 — 新版本追加於此即可 */
const RULE_FILE_INDEX: string[] = [
  'v2025-01-01.json',
  'p2026-09-01.json'
];

export const DENTAL_MOUNTAIN_ISLAND_AREA_CODES = MOUNTAIN_ISLAND_AREA_CODES;
export const DENTAL_MOUNTAIN_ISLAND_PLAN_AREA_CODES = DENTAL_MOUNTAIN_ISLAND_AREA_CODES;

export function normalizeDentalMountainIslandAreaCode(value: string): string {
  return normalizeMountainIslandAreaCode(value);
}

export function isDentalMountainIslandArea(value: string): boolean {
  return isMountainIslandArea(value);
}

export function normalizeDentalMountainIslandPlanAreaCode(value: string): string {
  return normalizeDentalMountainIslandAreaCode(value);
}

export function isDentalMountainIslandPlanArea(value: string): boolean {
  return isDentalMountainIslandArea(value);
}

// ============================================================
//  Service
// ============================================================

@Injectable({
  providedIn: 'root'
})
export class DentalAdditionApiService {
  private api: WebApiService;

  /** 已載入的 base 規則 (按 effectiveDate 升冪排列) */
  private baseRules: AdditionRuleSet[] = [];
  /** 已載入的 patch 規則 (按 effectiveDate 升冪排列) */
  private patchRules: AdditionRulePatch[] = [];
  /** 規則是否已載入 */
  private rulesLoaded = false;

  constructor(
    private http: HttpClient,
    private webApiService: WebApiService,
    private clinicData: ClinicDataService
  ) {
    this.api = new WebApiService(
      webApiService['http'],
      webApiService['mainLayoutService']
    );
    const config = BaseConfig.getConfig();
    this.api.baseUrl = config.webApi.dentistBaseUrl || config.webApi.hisBaseUrl;
  }

  // ============================================================
  //  規則載入
  // ============================================================

  /**
   * 載入所有規則 JSON 檔案 (登入後呼叫一次)
   * 讀取 rules/ 資料夾下的所有 JSON, 儲存到記憶體快取
   */
  async loadRules(): Promise<void> {
    if (this.rulesLoaded) return;

    const basePath = 'assets/dental-addition-rules/';
    const results = await Promise.all(
      RULE_FILE_INDEX.map(file =>
        this.http.get<AdditionRuleFile>(`${basePath}${file}?t=${Date.now()}`).toPromise()
          .catch(err => {
            console.warn(`[DentalAdditionApi] 無法載入規則檔 ${file}:`, err);
            return null;
          })
      )
    );

    for (const data of results) {
      if (!data) continue;
      if ('rules' in data) {
        this.baseRules.push(data as AdditionRuleSet);
      } else if ('patches' in data) {
        this.patchRules.push(data as AdditionRulePatch);
      }
    }

    // 按 effectiveDate 升冪排列
    const byDate = (a: { effectiveDate: string }, b: { effectiveDate: string }) =>
      a.effectiveDate.localeCompare(b.effectiveDate);
    this.baseRules.sort(byDate);
    this.patchRules.sort(byDate);

    this.rulesLoaded = true;
    console.log('[DentalAdditionApi] 規則載入完成', {
      baseRules: this.baseRules.length,
      patchRules: this.patchRules.length
    });
  }

  /** 確保規則已載入 (若尚未載入則嘗試載入) */
  private async ensureRulesLoaded(): Promise<void> {
    if (!this.rulesLoaded) {
      await this.loadRules();
    }
  }

  // ============================================================
  //  生效日檢核
  // ============================================================

  /**
   * 根據看診日取得有效的規則集
   * 邏輯: visitDate >= effectiveDate 的最新 base rule, 再疊加所有適用的 patches
   * @param visitDate 看診日 (Register.RegDate)
   */
  getEffectiveRuleSet(visitDate: Date): { ruleSet: AdditionRuleSet; warnings: string[] } {
    const warnings: string[] = [];

    // 找出 visitDate >= effectiveDate 的所有 base rules, 取最新的
    const applicableBases = this.baseRules.filter(r =>
      visitDate >= new Date(r.effectiveDate)
    );

    let ruleSet: AdditionRuleSet;
    if (applicableBases.length === 0) {
      // 無適用規則 → 使用最新版本 + 發出警告
      if (this.baseRules.length > 0) {
        ruleSet = this.deepCloneRuleSet(this.baseRules[this.baseRules.length - 1]);
        warnings.push(`看診日 ${this.formatDate(visitDate)} 早於最早規則生效日 ${ruleSet.effectiveDate}，已使用最新規則版本`);
      } else {
        // 完全沒有規則 — 回傳空殼 (不應該發生)
        warnings.push('未載入任何加成規則，請確認 rules/ 資料夾');
        return { ruleSet: this.getEmptyRuleSet(), warnings };
      }
    } else {
      ruleSet = this.deepCloneRuleSet(applicableBases[applicableBases.length - 1]);
    }

    // 疊加適用的 patches (effectiveDate <= visitDate)
    const applicablePatches = this.patchRules.filter(p =>
      visitDate >= new Date(p.effectiveDate)
    );
    for (const patch of applicablePatches) {
      this.applyPatch(ruleSet, patch);
    }

    return { ruleSet, warnings };
  }

  /** 套用 patch 到 ruleSet (修改 ruleSet in-place) */
  private applyPatch(ruleSet: AdditionRuleSet, patch: AdditionRulePatch): void {
    for (const p of patch.patches) {
      if (p.bonusType && p.rate !== undefined) {
        // 更新特定加成類型的 rate
        const bt = ruleSet.rules.bonusTypes.find(b => b.type === p.bonusType);
        if (bt) {
          bt.rate = p.rate;
        }
      }
      if (p.field && p.action && p.value !== undefined) {
        // 通用欄位更新 (如排除清單)
        const parts = p.field.split('.');
        let target: any = ruleSet.rules;
        for (let i = 0; i < parts.length - 1; i++) {
          target = target?.[parts[i]];
        }
        const lastKey = parts[parts.length - 1];
        if (target) {
          if (p.action === 'update') {
            target[lastKey] = p.value;
          } else if (p.action === 'add' && Array.isArray(target[lastKey])) {
            if (!target[lastKey].includes(p.value)) {
              target[lastKey].push(p.value);
            }
          } else if (p.action === 'remove' && Array.isArray(target[lastKey])) {
            target[lastKey] = target[lastKey].filter((x: any) => x !== p.value);
          }
        }
      }
    }
  }

  private deepCloneRuleSet(src: AdditionRuleSet): AdditionRuleSet {
    return JSON.parse(JSON.stringify(src));
  }

  private getEmptyRuleSet(): AdditionRuleSet {
    return {
      version: '0000-00-00',
      effectiveDate: '0000-00-00',
      description: 'Empty fallback',
      rules: {
        caseTypeMultipliers: { '19': 1, '16': 1, '14': 1.2, '12': 1 },
        bonusTypes: [],
        disabilityMultipliers: {
          levels: { profound_autism_dementia: 2.1, severe: 1.9, moderate: 1.5, mild: 1.3, lt: 1.2 },
          treatmentExclusions: [],
          examFeeCodeMap: {}
        },
        globalExclusions: { neverAddition: [], caseType14NoAdditions: [], caseType14ExclusionsOnlyBase: [], notForCaseType16: [] }
      }
    };
  }

  private formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // ============================================================
  //  核心加成計算 — 處置碼
  // ============================================================

  /**
   * 處置碼加成計算主進入點
   * 公式: finalPlus = caseMultiplier × (1 + bonusSum)
   */
  calculatePlus(ctx: AdditionContext): AdditionResult {
    const { ruleSet, warnings } = this.getEffectiveRuleSet(ctx.visitDate);
    const rules = ruleSet.rules;
    const reasons: string[] = [];

    const stdCode = ctx.stdCode || '';
    if (!stdCode) return { plus: 1.0, reasons: [], warnings };

    // ========== 完全不可加成 ==========
    if (rules.globalExclusions.neverAddition.includes(stdCode) || stdCode.length === 2) {
      reasons.push('不可加成項目');
      return { plus: 1.0, reasons, warnings };
    }

    // ========== 案類16特殊排除 ==========
    if (ctx.caseType === '16' && rules.globalExclusions.notForCaseType16.includes(stdCode)) {
      reasons.push('16案件不可加成代碼');
      return { plus: 1.0, reasons, warnings };
    }

    // ========== 案類基礎倍率 ==========
    let caseMultiplier = rules.caseTypeMultipliers[ctx.caseType] ?? 1.0;

    if (ctx.isDental) {
      const resolved = this.resolveDentalScenario(ctx);
      if (resolved) {
        if (resolved.multiplierSource !== 'disability') {
          caseMultiplier = resolved.effectiveMultiplier;
        }
        reasons.push(`[牙科情境] ${resolved.scenario.label}`);
      } else if (ctx.caseType === '14') {
        // 牙科案14必須符合 11 情境才可套用巡迴/社區 1.2。
        caseMultiplier = 1.0;
        reasons.push('[牙科情境] 未符合案14巡迴/社區情境');
      }
    }

    // 14案件特殊處理
    if (ctx.caseType === '14') {
      if (rules.globalExclusions.caseType14NoAdditions.includes(stdCode)) {
        reasons.push('14案件不可加成代碼');
        return { plus: 1.0, reasons, warnings };
      }
      if (rules.globalExclusions.caseType14ExclusionsOnlyBase.includes(stdCode)) {
        reasons.push('14案件僅基礎1.2倍');
        return { plus: caseMultiplier, reasons, warnings };
      }
    }

    // ========== 累加加成 (bonusSum) ==========
    let bonusSum = 0;

    for (const bt of rules.bonusTypes) {
      const bonus = this.evaluateBonus(bt, ctx, rules);
      if (bonus > 0) {
        bonusSum += bonus;
        reasons.push(bt.label);
      }
    }

    // ========== 身障加成 ==========
    const disabilityBonus = this.calculateDisabilityBonusPart(ctx, rules);
    if (disabilityBonus > 0) {
      bonusSum += disabilityBonus;
      reasons.push(`身障加成(+${(disabilityBonus * 100).toFixed(1)}%)`);
    }

    // ========== 最終倍率 ==========
    const finalPlus = caseMultiplier * (1.0 + bonusSum);
    // ========== 跨碼聯動加成調整（獨立規則） ==========
    const adjusted = this.applyCrossCodeAdjustment(finalPlus, ctx.stdCode, ctx.sameDayCodes, reasons);
    return { plus: adjusted, reasons, warnings };
  }

  // ============================================================
  //  核心加成計算 — 診察費
  // ============================================================

  /**
   * 診察費加成計算主進入點
   * 公式: finalPlus = caseMultiplier × (1 + bonusSum)
   */
  calculateExamFeePlus(ctx: AdditionContext): AdditionResult {
    const { ruleSet, warnings } = this.getEffectiveRuleSet(ctx.visitDate);
    const rules = ruleSet.rules;
    const reasons: string[] = [];

    const stdCode = ctx.stdCode || '';
    if (!stdCode) return { plus: 1.0, reasons: [], warnings };

    // 完全不可加成
    if (rules.globalExclusions.neverAddition.includes(stdCode) || stdCode.length === 2) {
      reasons.push('不可加成項目');
      return { plus: 1.0, reasons, warnings };
    }

    let caseMultiplier = 1.0;
    if (ctx.isDental) {
      const resolved = this.resolveDentalScenario(ctx);
      if (resolved) {
        caseMultiplier = resolved.multiplierSource === 'disability'
          ? 1.0
          : resolved.effectiveMultiplier;
        reasons.push(`[牙科情境] ${resolved.scenario.label}`);
      } else if (ctx.caseType === '14') {
        // 診察費的巡迴 1.2 也是獨立乘法倍率，不是 bonusSum +0.2。
        caseMultiplier = 1.0;
        reasons.push('[牙科情境] 未符合案14巡迴/社區情境');
      }
    }

    let bonusSum = 0;

    // 判斷轉診加成是否可用 — 先記錄，如有其他加成同時適用則一起加入
    let canReferral = false;
    const referralBt = rules.bonusTypes.find(b => b.type === 'referral');
    if (referralBt && ctx.isReferral) {
      const excl = referralBt.conditions.exclusions || [];
      if (new RegExp(referralBt.conditions.codePattern || '').test(stdCode) && !excl.includes(stdCode)) {
        if (!ctx.cannotApplyPlusCodes?.includes(stdCode)) {
          canReferral = true;
        }
      }
    }

    // 遍歷非轉診的加成類型
    for (const bt of rules.bonusTypes) {
      if (bt.type === 'referral') continue; // 診察費的轉診加成邏輯特殊，另行處理
      const bonus = this.evaluateBonus(bt, ctx, rules);
      if (bonus > 0) {
        bonusSum += bonus;
        reasons.push(bt.label);
        // 如果有其他加成同時適用轉診，一起加入
        if (canReferral) {
          bonusSum += referralBt!.rate;
          reasons.push(referralBt!.label);
          canReferral = false; // 避免重複加入
        }
      }
    }

    // 身障加成
    const disabilityBonus = this.calculateExamFeeDisabilityBonusPart(ctx, rules);
    if (disabilityBonus > 0) {
      bonusSum += disabilityBonus;
      reasons.push(`身障加成(+${(disabilityBonus * 100).toFixed(1)}%)`);
      if (canReferral) {
        bonusSum += referralBt!.rate;
        reasons.push(referralBt!.label);
        canReferral = false;
      }
    }

    // 若僅有轉診、無其他加成，仍加入轉診
    if (canReferral && bonusSum === 0) {
      bonusSum += referralBt!.rate;
      reasons.push('僅轉診加成');
    }

    const finalPlus = caseMultiplier * (1.0 + bonusSum);
    // ========== 跨碼聯動加成調整（獨立規則） ==========
    const adjusted = this.applyCrossCodeAdjustment(finalPlus, ctx.stdCode, ctx.sameDayCodes, reasons);
    return { plus: adjusted, reasons, warnings };
  }

  // ============================================================
  //  個別加成條件評估
  // ============================================================

  /** 評估單一加成是否適用，回傳加成增量 (0 表示不適用) */
  private evaluateBonus(bt: BonusTypeDef, ctx: AdditionContext, rules: AdditionRuleSet['rules']): number {
    const code = ctx.stdCode;
    const cond = bt.conditions;

    switch (bt.type) {
      case 'referral': {
        if (!ctx.isReferral) return 0;
        if (cond.codePattern && !new RegExp(cond.codePattern).test(code)) return 0;
        if (cond.exclusions?.includes(code)) return 0;
        if (ctx.cannotApplyPlusCodes?.includes(code)) return 0;
        return bt.rate;
      }
      case 'child': {
        // 優先使用月齡精確判定（<=60個月）；若無月齡資訊則 fallback 用整數歲
        const maxMonths = (cond.maxAge ?? 5) * 12;
        if (ctx.ageInMonths != null) {
          if (ctx.ageInMonths > maxMonths) return 0;
        } else {
          if (ctx.age >= (cond.maxAge ?? 5) + 1) return 0;
        }
        if (cond.excludeCodes?.includes(code)) return 0;
        if (cond.excludeExamFee && this.isExamFeeCode(code, ctx.codeName)) return 0;
        return bt.rate;
      }
      case 'anesthesia-infant': {
        if (!cond.codes?.includes(code)) return 0;
        if (ctx.age >= (cond.maxAge ?? 2)) return 0;
        return bt.rate;
      }
      case 'anesthesia-child': {
        if (!cond.codes?.includes(code)) return 0;
        if (ctx.age < (cond.minAge ?? 2) || ctx.age >= (cond.maxAge ?? 7)) return 0;
        return bt.rate;
      }
      case 'senior-scaling': {
        if (ctx.age < (cond.minAge ?? 40)) return 0;
        if (!cond.codes?.includes(code)) return 0;
        return bt.rate;
      }
      case 'senior-root': {
        if (ctx.age < (cond.minAge ?? 65)) return 0;
        if (!cond.codes?.includes(code)) return 0;
        return bt.rate;
      }
      case 'island-holiday': {
        if (!ctx.isHoliday) return 0;
        if (cond.nameContains && !ctx.codeName?.includes(cond.nameContains)) return 0;
        const nameOk = ctx.codeName?.includes('山地離島');
        const caseOk = cond.caseTypes?.includes(ctx.caseType);
        if (!nameOk && !caseOk) return 0;
        return bt.rate;
      }
      case 'night-emergency': {
        if (!ctx.isNight) return 0;
        if (cond.caseTypes && !cond.caseTypes.includes(ctx.caseType)) return 0;
        if (cond.codes && !cond.codes.includes(code)) return 0;
        return bt.rate;
      }
      default:
        return 0;
    }
  }

  private isExamFeeCode(code: string, codeName?: string): boolean {
    return /^001|^003/.test(code) || (codeName?.includes('診察') ?? false);
  }

  // ============================================================
  //  跨碼聯動加成調整 (獨立於一般加成規則之外)
  // ============================================================

  /** 觸發跨碼聯動調整的處置碼（同一天存在任一即觸發） */
  private static readonly CROSS_CODE_TRIGGERS: string[] = [
    '92013C', '92014C', '92015C', '92016C', '92055C', '92063C', '92092C'
  ];

  /**
   * 完全獨立於一般加成規則之外的跨碼聯動加成調整。
   * - 若同一天存在任一觸發碼 (CROSS_CODE_TRIGGERS)：
   *   - 92041C 的當前 plus × 0.5（直接減半）
   *   - 91011C 的當前 plus × 0（直接歸零）
   * - 此規則以「乘」的方式套用於既有 plus，可與任何加成規則並存。
   */
  private applyCrossCodeAdjustment(plus: number, stdCode: string, sameDayCodes?: string[], reasons?: string[]): number {
    if (!stdCode) return plus;
    if (!sameDayCodes || sameDayCodes.length === 0) return plus;
    if (stdCode !== '92041C' && stdCode !== '91011C') return plus;

    const triggers = DentalAdditionApiService.CROSS_CODE_TRIGGERS;
    const hasTrigger = sameDayCodes.some(c => !!c && c !== stdCode && triggers.includes(c));
    if (!hasTrigger) return plus;

    if (stdCode === '92041C') {
      reasons?.push('同日含跨碼觸發碼，92041C 減半 (×0.5)');
      return plus * 0.5;
    }
    if (stdCode === '91011C') {
      reasons?.push('同日含跨碼觸發碼，91011C 歸零 (×0)');
      return 0;
    }
    return plus;
  }

  // ============================================================
  //  身障加成計算 (從 DentalDisabilityMemoService 搬移)
  // ============================================================

  /**
   * 處置碼的身障加成增量 (倍率 - 1)
   * @returns 加成增量 (如 1.1 表示 +110%)
   */
  private calculateDisabilityBonusPart(ctx: AdditionContext, rules: AdditionRuleSet['rules']): number {
    // 案類16專用排除先於身障與LT倍率計算，一般加成仍照常套用。
    if (ctx.caseType === '16' && (ctx.stdCode === 'P7303C' || rules.disabilityMultipliers.caseType16TreatmentExclusions?.includes(ctx.stdCode))) {
      return 0;
    }

    const multiplier = this.calculateTreatmentMultiplier(ctx.stdCode, ctx.disabilityData, rules);
    return multiplier > 1.0 ? multiplier - 1.0 : 0;
  }

  /**
   * 診察費的身障加成增量 (倍率 - 1)
   */
  private calculateExamFeeDisabilityBonusPart(ctx: AdditionContext, rules: AdditionRuleSet['rules']): number {
    const multiplier = this.calculateExamFeeMultiplier(ctx.stdCode, ctx.disabilityData, rules);
    return multiplier > 1.0 ? multiplier - 1.0 : 0;
  }

  /**
   * 計算診察費身障倍率
   * 公開方法 — 供 DentalDisabilityMemoService 委派呼叫
   */
  calculateExamFeeMultiplier(examFeeCode: string, disabilityData: DisabilityInput | null | undefined, rules?: AdditionRuleSet['rules']): number {
    if (!disabilityData?.disabilityScores || !disabilityData?.selectedServiceMode) return 1.0;
    if (!disabilityData.matchedTreatmentItem?.trim()) return 1.0;

    const r = rules || this.getLatestRules();
    const examFeeMap = r.disabilityMultipliers.examFeeCodeMap;

    if (examFeeMap[examFeeCode] !== undefined) {
      const mapped = examFeeMap[examFeeCode];
      if (mapped === 'flexible') {
        return this.getMultiplierByDisabilityLevel(disabilityData, r);
      }
      return mapped as number;
    }

    return 1.0;
  }

  /**
   * 計算處置碼身障倍率
   * 公開方法 — 供 DentalDisabilityMemoService 委派呼叫
   */
  calculateTreatmentMultiplier(treatmentCode: string, disabilityData: DisabilityInput | null | undefined, rules?: AdditionRuleSet['rules']): number {
    if (!disabilityData?.disabilityScores || !disabilityData?.selectedServiceMode) return 1.0;
    if (!disabilityData.matchedTreatmentItem?.trim()) return 1.0;

    const r = rules || this.getLatestRules();

    if (disabilityData.matchedTreatmentItem === 'LT') {
      return r.disabilityMultipliers.levels.lt;
    }

    if (r.disabilityMultipliers.treatmentExclusions.includes(treatmentCode)) {
      return 1.0;
    }

    return this.getMultiplierByDisabilityLevel(disabilityData, r);
  }

  /**
   * 根據障礙評分和服務模式決定加成倍率
   */
  getMultiplierByDisabilityLevel(data: DisabilityInput, rules?: AdditionRuleSet['rules']): number {
    const r = rules || this.getLatestRules();
    const levels = r.disabilityMultipliers.levels;

    let scores = data.disabilityScores?.split(',').map(s => s.trim()).filter(s => s) || [];

    // 居家模式下植物人輕度(09.1)排除
    if (data.selectedServiceMode === 'home-care') {
      scores = scores.filter(s => s !== '09.1');
    }
    // 院內或醫缺巡迴下失能老人(16.1)排除
    if (data.selectedServiceMode === 'in-clinic' || data.selectedServiceMode === 'mobile-clinic') {
      scores = scores.filter(s => s !== '16.1');
    }

    const has極重度 = scores.some(s => this.is極重度(s));
    const has失智或自閉 = scores.some(s => this.is失智或自閉(s));
    const has重度 = scores.some(s => this.is重度(s));
    const has中度 = scores.some(s => this.is中度(s));
    const has中度以上精神 = scores.some(s => this.is中度以上精神(s));
    const has發展遲緩 = scores.includes('16.2');
    const has腦傷脊髓中度肢體 = scores.some(s => this.is腦傷脊髓中度肢體(s));
    const has輕度 = scores.some(s => this.is輕度(s));
    const has失能老人 = scores.includes('16.1');

    if (has極重度 || has失智或自閉) return levels.profound_autism_dementia;
    if (has重度) return levels.severe;
    if (has中度 || has中度以上精神 || has發展遲緩 || has腦傷脊髓中度肢體) return levels.moderate;
    if (has輕度 || has失能老人) return levels.mild;

    return 1.0;
  }

  /** 取得最新的規則 (快取中最後一份 base) */
  private getLatestRules(): AdditionRuleSet['rules'] {
    if (this.baseRules.length > 0) {
      return this.baseRules[this.baseRules.length - 1].rules;
    }
    return this.getEmptyRuleSet().rules;
  }

  /**
   * 判斷同日處置是否包含 LM。
   * 規則由 JSON lmRule 控制，避免把 P7101C/P7102C 與年齡條件寫死在 UI。
   */
  detectHasLMRx(sameDayCodes: string[] | undefined, age: number, rules?: AdditionRuleSet['rules']): boolean {
    if (!sameDayCodes || sameDayCodes.length === 0) return false;
    const r = rules || this.getLatestRules();
    const lm = r.lmRule;
    if (!lm) return false;
    if (age < lm.minAge || age > lm.maxAge) return false;
    return sameDayCodes.some(code => !!code && lm.codes.includes(code));
  }

  /**
   * 依 JSON dentalScenarios 解析牙科 11 情境。
   * 巡迴不是 +0.2 bonus，而是情境自身的獨立 multiplier，例如 1.2。
   */
  resolveDentalScenario(ctx: AdditionContext, disabilitySptxCode?: string): ResolvedDentalScenario | null {
    if (!ctx.isDental) return null;

    const rules = this.getEffectiveRuleSet(ctx.visitDate).ruleSet.rules;
    const block = rules.dentalScenarios;
    if (!block?.list?.length) return null;

    const flags: { [K in keyof DentalScenarioMatch]: boolean } = {
      isClinicLack: !!ctx.isClinicLack,
      isMobile: ctx.specAreaSV === '02',
      isCommunityStation: !!ctx.isCommunityStation,
      hasLMRx: !!ctx.hasLMRx,
      isDisability: !!ctx.isDisability,
    };

    const scenario = block.list.find(item => this.matchScenario(item.match, flags));
    if (!scenario) return null;

    const sptxResolved = scenario.result.sptxOrdered
      .map(code => code === '__DISABILITY__' ? (disabilitySptxCode || '') : code)
      .filter(code => !!code);

    const isIslandHolidayActive = !!ctx.isHoliday && !!ctx.isIsland;
    const canUseIslandHolidayMutex = ['S2-mobile', 'S3-community', 'S4-clinic-mobile'].includes(scenario.id);

    let effectiveMultiplier: number;
    let multiplierSource: ResolvedDentalScenario['multiplierSource'] = 'scenario';
    if (scenario.result.multiplier === 'disability') {
      effectiveMultiplier = 1.0;
      multiplierSource = 'disability';
    } else if (isIslandHolidayActive && canUseIslandHolidayMutex) {
      effectiveMultiplier = 1.0;
      multiplierSource = 'island-holiday-override';
    } else {
      effectiveMultiplier = scenario.result.multiplier;
    }

    return {
      scenario,
      effectiveMultiplier,
      multiplierSource,
      sptxResolved,
      p8: scenario.result.p8,
    };
  }

  private matchScenario(match: DentalScenarioMatch, flags: { [K in keyof DentalScenarioMatch]: boolean }): boolean {
    for (const key of Object.keys(match) as (keyof DentalScenarioMatch)[]) {
      const expected = match[key];
      if (expected === undefined || expected === null) continue;
      if (flags[key] !== expected) return false;
    }
    return true;
  }

  /** 取得最新完整規則集 (公開供 simulator 使用) */
  getLatestRuleSet(): AdditionRuleSet {
    if (this.baseRules.length > 0) {
      return this.baseRules[this.baseRules.length - 1];
    }
    return this.getEmptyRuleSet();
  }

  // --- 障礙評分判定子方法 ---

  private is極重度(score: string): boolean {
    if (score.startsWith('12.')) return false;
    if (score.startsWith('10.') || score.startsWith('11.')) return false;
    return score.endsWith('.4');
  }

  private is失智或自閉(score: string): boolean {
    return score.startsWith('10.') || score.startsWith('11.');
  }

  private is重度(score: string): boolean {
    if (score.startsWith('12.')) return false;
    if (score.startsWith('10.') || score.startsWith('11.')) return false;
    return score.endsWith('.3');
  }

  private is中度(score: string): boolean {
    if (score.startsWith('12.')) return false;
    if (score.startsWith('10.') || score.startsWith('11.')) return false;
    if (score === '05-cerebral.2') return false;
    return score.endsWith('.2');
  }

  private is中度以上精神(score: string): boolean {
    return score === '12.2' || score === '12.3' || score === '12.4';
  }

  private is腦傷脊髓中度肢體(score: string): boolean {
    return score === '05-cerebral.2';
  }

  private is輕度(score: string): boolean {
    if (score.startsWith('12.')) return false;
    if (score.startsWith('10.') || score.startsWith('11.')) return false;
    return score.endsWith('.1');
  }

  // ============================================================
  //  支付成數 (供申報用)
  // ============================================================

  getPaymentRatio(disabilityData: DisabilityInput | null): string {
    const multiplier = this.getMultiplierByDisabilityLevel(disabilityData || {} as any);
    return multiplier.toFixed(1);
  }

  // ============================================================
  //  代碼查詢 (既有功能保留)
  // ============================================================

  getDentalCodes(): Observable<DentalCode[]> {
    return from(this.clinicData.GetRxOption()).pipe(
      map((rxOptions: RxOpt[]) => {
        return rxOptions
          .filter(rx => this.isDentalCode(rx))
          .map(rx => ({
            Code: rx.RxCode || rx.StdCode,
            Name: rx.ProdName,
            Points: rx.IPrice || rx.Price || 0
          }));
      }),
      catchError(error => {
        console.error('取得牙科診療代碼失敗:', error);
        return of([]);
      })
    );
  }

  getDentalExamFeeCodes(): Observable<DentalCode[]> {
    return from(this.clinicData.GetRxOption()).pipe(
      map((rxOptions: RxOpt[]) => {
        const examFeeRaw = rxOptions.filter(rx => rx.Type === OrderTypeEnum.T1_ConsultationFee);
        const fallback = rxOptions.filter(rx =>
          (rx.Type == null || rx.Type === 0) && this.looksLikeExamFee(rx)
        );
        const merged = [...examFeeRaw, ...fallback];
        const uniq = new Map<string, RxOpt>();
        merged.forEach(rx => {
          const key = rx.RxCode || rx.StdCode;
          if (!key) return;
          if (!uniq.has(key)) uniq.set(key, rx);
        });
        return Array.from(uniq.values()).map(rx => ({
          Code: rx.RxCode || rx.StdCode,
          Name: rx.ProdName,
          Points: rx.IPrice || rx.Price || 0
        }));
      }),
      catchError(error => {
        console.error('取得牙科診察費代碼失敗:', error);
        return of([]);
      })
    );
  }

  private isDentalCode(rx: RxOpt): boolean {
    const dentalPrefixes = ['89', '90', '91', '92', '96', 'P7', 'P3'];
    const rxCode = rx.RxCode || rx.StdCode || '';
    return dentalPrefixes.some(prefix => rxCode.startsWith(prefix));
  }

  private looksLikeExamFee(rx: RxOpt): boolean {
    const rxCode = rx.RxCode || rx.StdCode || '';
    const name = rx.ProdName || '';
    const examPrefixes = ['003', '001'];
    return name.includes('診察') || examPrefixes.some(p => rxCode.startsWith(p));
  }
}
